import React, {useEffect} from "react";
import {
    IApplicationInternal, IApplicationToken,
    IScope
} from "@src/Components/Pages/Main/Content/ApplicationsPage/Interfaces/interfaces";
import {useFormik, FormikProvider} from "formik";
import {FormControlLabel, Grid, Switch} from "@material-ui/core";
import {FullWidthTextField} from "@src/Shared/styles";
import {Values} from "Tools/Values";
import TransferList from "@src/Components/Common/TransferList/TransferList";
import {LogoField} from "./LogoField/LogoField";
import {IApplicationsStore} from "Stores/Applications/types";
import {TokenField} from "./TokenField/TokenField";

interface IProps {
    initialValues?: IApplicationInternal;
    isEdit: boolean
    onSubmit: (data: any) => void;
    setRef: (ref: any) => void;
    scopes: IScope[];
    applicationsStore?: IApplicationsStore;
    onTokenRefresh: (newToken: IApplicationToken) => void;
}

export const InternalAppDialogForm = (props: IProps) => {
    const formik = useFormik({
        initialValues: {
            displayName: '',
            description: '',
            displayInDrawer: false,
            image: '',
            url: '',
            scopes: [] as string[],
            imageFile: null,
        },
        onSubmit: (data) => props.onSubmit(data),
        validateOnChange: false,
        validateOnBlur: false
    });

    useEffect(() => {
        if (props.initialValues && props.isEdit) {
            const scopes = props.initialValues.scopes?.map(scope => scope.id);
            formik.setValues({...props.initialValues, imageFile: null, scopes})
        }
    }, [props.initialValues]);

    const getSelectedScopes = () => {
        if (props.initialValues && props.isEdit) {
            return props.initialValues.scopes;
        }

        return [] as IScope[];
    };

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} ref={ref => props.setRef(ref)}>
                <Grid container spacing={2} direction="column">
                    <Grid item>
                        <FullWidthTextField
                            name="displayName"
                            onChange={formik.handleChange}
                            value={formik.values.displayName}
                            size="small"
                            error={formik.errors.displayName}
                            helperText={formik.errors.displayName}
                            label={Values.getLocalizationValue('name')}
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item>
                        <FullWidthTextField
                            name="description"
                            onChange={formik.handleChange}
                            value={formik.values.description}
                            size="small"
                            error={formik.errors.description}
                            helperText={formik.errors.description}
                            label={Values.getLocalizationValue('description')}
                            variant="outlined"
                            multiline
                            rows={3}
                        />
                    </Grid>

                    <Grid item>
                        <TransferList
                            fieldName="scopes"
                            label={Values.getLocalizationValue('scopes')}
                            formikProps={formik}
                            allItems={props.scopes}
                            selectedItems={getSelectedScopes()}
                            keyAsValue="id"
                            keyAsTitle="friendlyName"
                        />
                    </Grid>

                    <Grid item>
                        <LogoField
                            fieldName="image"
                            fileFieldName="imageFile"
                            label={Values.getLocalizationValue('image')}
                            imageUrl={props.initialValues?.image}
                            hasImageSet
                        />
                    </Grid>

                    <Grid item>
                        <FullWidthTextField
                            name="url"
                            onChange={formik.handleChange}
                            value={formik.values.url}
                            size="small"
                            error={formik.errors.url}
                            helperText={formik.errors.url}
                            label={Values.getLocalizationValue('applicationUrl')}
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item>
                        <FormControlLabel
                            label={Values.getLocalizationValue('displayInAppDrawer')}
                            control={
                                <Switch
                                    color="primary"
                                    name="displayInDrawer"
                                    onChange={formik.handleChange}
                                    checked={formik.values.displayInDrawer}
                                />
                            }
                        />
                    </Grid>
                    {props.isEdit && (
                        <>
                            <Grid item>
                                <FullWidthTextField
                                    name="clientId"
                                    value={props.initialValues.clientId}
                                    size="small"
                                    disabled
                                    label={Values.getLocalizationValue('id')}
                                    variant="outlined"
                                />
                            </Grid>

                            <Grid item>
                                <TokenField
                                    app={props.initialValues}
                                    onTokenRefresh={(token) => props.onTokenRefresh(token)}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </form>
        </FormikProvider>
    )
}
